import {
  addDays,
  compareDates,
  inRange,
  lastSmokeAt,
  limitOn,
  limitProgress,
  localDate,
  phaseOn,
  presetRange,
  quitDateOf,
  savedVsBaseline,
  segmentOn,
  smokeFreeProgress,
  spentMinor,
  tallyRange,
  upcomingSteps,
  type CravingLike,
  type LimitProgress,
  type LocalDate,
  type QuitPhase,
  type QuitPlan,
  type SmokeFreeProgress,
  type SmokeLogLike,
  type TaperStep,
  type WeekStart,
} from '@tahi/core';

export interface QuitOverview {
  today: LocalDate;
  phase: QuitPhase;
  count: number;
  limit: number | null;
  tomorrowLimit: number | null;
  quitDate: LocalDate | null;
  nextStep: TaperStep | null;
  /** The floor of a plan that cuts down without quitting, otherwise null. */
  floor: number | null;
  lastSmokeAt: string | null;
  minutesSince: number | null;
  hasPrice: boolean;
  spentToday: number;
  spentWeek: number;
  spentAll: number;
  saved: number;
  week: { date: LocalDate; count: number; limit: number | null }[];
  limitProgress: LimitProgress;
  smokeFree: SmokeFreeProgress;
  resistedWeek: number;
  /** Average a day over the last 7 completed days (since the plan or the first log); null with none. */
  weekAverage: number | null;
  /** The taper for the progress chart: every day from the plan start to the quit date (or four
   * weeks ahead), with the count for days up to today. A taper longer than 180 days shows a window
   * that keeps today and the 30 days before it. */
  taperDays: { date: LocalDate; count: number | null; limit: number | null }[];
}

/** At most this many days in the taper chart. */
const MAX_CHART_DAYS = 180;
/** On a taper longer than the chart, at least this many days before today stay in view. */
const CHART_PAST_DAYS = 30;

export interface OverviewInput {
  plan: QuitPlan;
  logs: readonly SmokeLogLike[];
  cravings: readonly CravingLike[];
  now: string;
  tz: string;
  weekStart: WeekStart;
}

export function buildOverview({
  plan,
  logs,
  cravings,
  now,
  tz,
  weekStart,
}: OverviewInput): QuitOverview {
  const today = localDate(now, tz);
  const earliest = logs.reduce(
    (min, l) => (compareDates(l.date, min) < 0 ? l.date : min),
    plan.startDate,
  );
  const all = tallyRange(logs, {
    start: compareDates(earliest, today) <= 0 ? earliest : today,
    end: today,
  });
  const todayTally = all.at(-1);
  const weekStartDate = presetRange('week', today, weekStart).start;
  const thisWeek = { start: weekStartDate, end: today };
  const last7 = tallyRange(logs, { start: addDays(today, -6), end: today });
  const quitDate = quitDateOf(plan.taper);
  const last = lastSmokeAt(logs, tz);
  const counts = new Map(all.map((t) => [t.date, t.count]));
  const completed7 = all.filter(
    (t) => compareDates(t.date, today) < 0 && compareDates(t.date, addDays(today, -7)) >= 0,
  );
  const weekAverage =
    completed7.length === 0
      ? null
      : Math.round((completed7.reduce((sum, t) => sum + t.count, 0) / completed7.length) * 10) / 10;
  const ahead = quitDate && compareDates(quitDate, today) > 0 ? quitDate : addDays(today, 28);
  const chartEnd = quitDate && compareDates(quitDate, today) <= 0 ? addDays(today, 7) : ahead;
  // A long taper doesn't fit: show the latest window that still keeps today and the recent past
  // (never before the plan start), cut short at the far end if needed.
  const latestStart = addDays(chartEnd, -(MAX_CHART_DAYS - 1));
  const pastStart = addDays(today, -CHART_PAST_DAYS);
  const windowStart = compareDates(latestStart, pastStart) < 0 ? latestStart : pastStart;
  const chartStart = compareDates(windowStart, plan.startDate) > 0 ? windowStart : plan.startDate;
  const windowEnd = addDays(chartStart, MAX_CHART_DAYS - 1);
  const lastDay = compareDates(windowEnd, chartEnd) < 0 ? windowEnd : chartEnd;
  const taperDays: QuitOverview['taperDays'] = [];
  for (let d = chartStart; compareDates(d, lastDay) <= 0; d = addDays(d, 1)) {
    taperDays.push({
      date: d,
      count: compareDates(d, today) <= 0 ? (counts.get(d) ?? 0) : null,
      limit: limitOn(plan.taper, d),
    });
  }

  return {
    today,
    phase: phaseOn(plan.taper, today),
    count: todayTally?.count ?? 0,
    limit: limitOn(plan.taper, today),
    tomorrowLimit: limitOn(plan.taper, addDays(today, 1)),
    quitDate,
    nextStep: upcomingSteps(plan.taper, today, 1)[0] ?? null,
    floor: quitDate === null ? (segmentOn(plan.taper, today)?.floor ?? null) : null,
    lastSmokeAt: last,
    minutesSince:
      last === null ? null : Math.max(0, Math.floor((Date.parse(now) - Date.parse(last)) / 60_000)),
    hasPrice: plan.price.kind !== 'none',
    spentToday: todayTally?.costMinor ?? 0,
    spentWeek: spentMinor(all.filter((t) => inRange(t.date, thisWeek))),
    spentAll: spentMinor(all),
    saved: savedVsBaseline(plan, all, today),
    week: last7.map((t) => ({ date: t.date, count: t.count, limit: limitOn(plan.taper, t.date) })),
    limitProgress: limitProgress(plan, all, today),
    smokeFree: smokeFreeProgress(plan, all, today),
    resistedWeek: cravings.filter(
      (c) => c.deletedAt === null && c.resisted && inRange(c.date, thisWeek),
    ).length,
    weekAverage,
    taperDays,
  };
}
